/**
 * useDeployment - convenience hook over the deployment descriptor.
 *
 * Wraps the RTK Query call so components can ask simple questions
 * ("is this a control deployment?") without each repeating the query
 * and the loading/default handling.
 *
 * Default-safe: until the descriptor loads, `isControl` is false and
 * `isClient` is false - neither control-only nor client-only UI flashes
 * before we know. Components that must wait can read `isLoading`.
 *
 * The pure derivation (`deriveDeploymentFlags`) is split out and exported
 * so it can be unit-tested without a store or a mocked fetch.
 */
import { useGetDeploymentInfoQuery } from "../api/deploymentApi";
import type { DeploymentInfo, DeploymentMode } from "../model/types";

export interface DeploymentFlags {
  mode: DeploymentMode | null;
  /** True only once the descriptor has loaded AND mode === "control". */
  isControl: boolean;
  /** True only once the descriptor has loaded AND mode === "client". */
  isClient: boolean;
  /** The single tenant slug on a client deployment; null otherwise. */
  tenantSlug: string | null;
}

export interface UseDeploymentResult extends DeploymentFlags {
  isLoading: boolean;
}

/**
 * Pure mapping from the raw descriptor (or undefined, while loading) to
 * the derived flags. Default-safe: undefined input yields all-false /
 * all-null, so no mode-specific UI renders before the descriptor lands.
 */
export function deriveDeploymentFlags(
  data: DeploymentInfo | undefined,
): DeploymentFlags {
  return {
    mode: data?.mode ?? null,
    isControl: data?.mode === "control",
    isClient: data?.mode === "client",
    tenantSlug: data?.tenant_slug ?? null,
  };
}

/** Hook: deployment descriptor questions ("is this a control deployment?"), default-safe while loading. */
export function useDeployment(): UseDeploymentResult {
  const { data, isLoading } = useGetDeploymentInfoQuery();
  return { isLoading, ...deriveDeploymentFlags(data) };
}
